import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from './api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [featureFlags, setFeatureFlags] = useState({});
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem('auth_token'));

  // Default permissions for different scenarios
  const defaultStaffPermissions = {
    role: 'staff',
    can_manage_employees: false,
    can_manage_shifts: false,
    can_manage_pay_rates: false,
    can_view_costs: false,
    can_manage_settings: false,
    can_manage_users: false,
    can_access_admin_dashboard: false,
    can_view_billing: false
  };

  const defaultAdminPermissions = {
    role: 'super_admin',
    can_manage_employees: true,
    can_manage_shifts: true,
    can_manage_pay_rates: true,
    can_view_costs: true,
    can_manage_settings: true,
    can_manage_users: true,
    can_access_admin_dashboard: true,
    can_view_billing: true
  };

  // Logout handler - defined early so it can be used in interceptor
  const handleLogout = useCallback(() => {
    localStorage.removeItem('auth_token');
    setToken(null);
    setUser(null);
    setCompany(null);
    setPermissions(null);
    setSubscription(null);
    setFeatureFlags({});
  }, []);

  // Set up axios interceptor for auth header
  useEffect(() => {
    const requestInterceptor = api.interceptors.request.use(
      (config) => {
        const storedToken = localStorage.getItem('auth_token');
        if (storedToken) {
          config.headers.Authorization = `Bearer ${storedToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    const responseInterceptor = api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Token expired or invalid
          handleLogout();
        }
        return Promise.reject(error);
      }
    );

    return () => {
      api.interceptors.request.eject(requestInterceptor);
      api.interceptors.response.eject(responseInterceptor);
    };
  }, [handleLogout]);

  // Fetch subscription info
  const fetchSubscription = useCallback(async () => {
    try {
      const response = await api.get('/subscription');
      setSubscription(response.data);
      // Extract feature flags from subscription response
      if (response.data.feature_flags) {
        setFeatureFlags(response.data.feature_flags);
      }
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
    }
  }, []);

  // Check if user is authenticated on mount
  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = localStorage.getItem('auth_token');
      if (storedToken) {
        try {
          const response = await api.get('/auth/me');
          setUser(response.data.user);
          setCompany(response.data.company);
          setPermissions(response.data.permissions);
          
          // Fetch subscription if not platform admin
          if (!response.data.user?.is_platform_admin) {
            const subResponse = await api.get('/subscription');
            setSubscription(subResponse.data);
            // Also set feature flags from subscription
            if (subResponse.data.feature_flags) {
              setFeatureFlags(subResponse.data.feature_flags);
            }
          }
        } catch (error) {
          console.error('Auth check failed:', error);
          localStorage.removeItem('auth_token');
          setToken(null);
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = useCallback(async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { token: newToken, user: userData, company: companyData, permissions: perms } = response.data;
    
    localStorage.setItem('auth_token', newToken);
    setToken(newToken);
    setUser(userData);
    setCompany(companyData);
    
    // Set permissions from login response if available (for admin users)
    if (perms) {
      setPermissions(perms);
    }
    
    // Fetch permissions and subscription
    const meResponse = await api.get('/auth/me');
    setPermissions(meResponse.data.permissions);
    
    if (!userData.is_platform_admin) {
      const subResponse = await api.get('/subscription');
      setSubscription(subResponse.data);
      // Also set feature flags from subscription
      if (subResponse.data.feature_flags) {
        setFeatureFlags(subResponse.data.feature_flags);
      }
    }
    
    return response.data;
  }, []);

  const staffLogin = useCallback(async (companyCode, employeeId, password) => {
    const response = await api.post('/auth/staff-login', {
      company_code: companyCode,
      employee_id: employeeId,
      password
    });
    const { token: newToken, user: userData, company: companyData, feature_flags: flags, permissions: perms } = response.data;
    
    localStorage.setItem('auth_token', newToken);
    setToken(newToken);
    setUser(userData);
    setCompany(companyData);
    
    // Set permissions from login response
    if (perms) {
      setPermissions(perms);
    }
    
    // Set feature flags from login response if available
    if (flags) {
      setFeatureFlags(flags);
    }
    
    // Fetch permissions and subscription
    const meResponse = await api.get('/auth/me');
    if (meResponse.data.permissions) {
      setPermissions(meResponse.data.permissions);
    }
    
    const subResponse = await api.get('/subscription');
    setSubscription(subResponse.data);
    // Also set feature flags from subscription
    if (subResponse.data.feature_flags) {
      setFeatureFlags(subResponse.data.feature_flags);
    }
    
    return response.data;
  }, []);

  // Staff login with email (for returning users)
  const staffEmailLogin = useCallback(async (email, password) => {
    const response = await api.post('/auth/staff-email-login', {
      email,
      password
    });
    const { token: newToken, user: userData, company: companyData, feature_flags: flags, permissions: perms } = response.data;
    
    localStorage.setItem('auth_token', newToken);
    setToken(newToken);
    setUser(userData);
    setCompany(companyData);
    
    // Set permissions from login response
    if (perms) {
      setPermissions(perms);
    }
    
    // Set feature flags from login response
    if (flags) {
      setFeatureFlags(flags);
    }
    
    // Fetch additional data
    const meResponse = await api.get('/auth/me');
    if (meResponse.data.permissions) {
      setPermissions(meResponse.data.permissions);
    }
    
    const subResponse = await api.get('/subscription');
    setSubscription(subResponse.data);
    if (subResponse.data.feature_flags) {
      setFeatureFlags(subResponse.data.feature_flags);
    }
    
    return response.data;
  }, []);

  const register = useCallback(async (name, email, password, country_code = "GB") => {
    const response = await api.post('/auth/register', { name, email, password, country_code });
    const { token: newToken, user: userData, company: companyData } = response.data;
    
    localStorage.setItem('auth_token', newToken);
    setToken(newToken);
    setUser(userData);
    setCompany(companyData);
    
    // Fetch permissions and subscription
    const meResponse = await api.get('/auth/me');
    setPermissions(meResponse.data.permissions);
    
    const subResponse = await api.get('/subscription');
    setSubscription(subResponse.data);
    
    return response.data;
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data.user);
      setCompany(response.data.company);
      setPermissions(response.data.permissions);
      
      if (!response.data.user?.is_platform_admin) {
        await fetchSubscription();
      }
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  }, [fetchSubscription]);

  // Helper to check feature access using dynamic feature flags from API
  const hasFeatureAccess = useCallback((feature) => {
    // Use dynamic feature flags from the subscription API
    if (featureFlags && Object.keys(featureFlags).length > 0) {
      return featureFlags[feature] === true;
    }
    // Fallback: trial users get standard features
    if (company?.subscription_status === 'trial') {
      // Default trial features (same as standard - full access)
      const trialFeatures = {
        dayView: true, weekView: true, basicReports: true, advancedReports: true,
        wageCostCalculations: true, breakManagement: true, overtimeRates: true,
        absenceTracking: true, csvExport: true, pdfExport: true, eventManagement: true,
        clockInOut: false, payrollIntegrations: false, multipleLocations: false, multipleDepartments: false
      };
      return trialFeatures[feature] === true;
    }
    return false;
  }, [featureFlags, company]);

  // Helper to check role-based permissions
  const hasPermission = useCallback((permission) => {
    // Company admins have all permissions
    if (user?.is_company_admin) {
      return true;
    }
    // Platform admins don't have company-level permissions
    if (user?.is_platform_admin) {
      return false;
    }
    // Check specific permission from permissions object
    if (permissions && permissions[permission] !== undefined) {
      return permissions[permission] === true;
    }
    return false;
  }, [permissions, user]);

  // Check if user can access the manager/admin dashboard
  const canAccessAdminDashboard = useCallback(() => {
    if (user?.is_company_admin) return true;
    if (user?.is_admin_user) return true;
    if (user?.is_platform_admin) return false;
    return permissions?.can_access_admin_dashboard === true;
  }, [permissions, user]);

  const value = {
    user,
    company,
    permissions,
    subscription,
    featureFlags,
    token,
    loading,
    isAuthenticated: !!token && !!user,
    isPlatformAdmin: user?.is_platform_admin === true,
    isCompanyAdmin: user?.is_company_admin === true,
    isAdminUser: user?.is_admin_user === true,
    trialDaysRemaining: company?.trial_days_remaining ?? 0,
    subscriptionTier: subscription?.subscription_tier || 
                      (company?.subscription_status === 'trial' ? 'trial' : 'light'),
    login,
    staffLogin,
    staffEmailLogin,
    register,
    logout: handleLogout,
    refreshUser,
    refreshAuth: refreshUser, // Alias for refreshUser
    fetchSubscription,
    hasFeatureAccess,
    hasPermission,
    canAccessAdminDashboard
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
